import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { User, Building2, CreditCard, Key, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

export default function AccountPage() {
  const { user, profile, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    full_name: '',
    practice_name: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    npi: '',
    specialty: '',
    website: '',
  });

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        practice_name: profile.practice_name || '',
        phone: profile.phone || '',
        address: profile.address || '',
        city: profile.city || '',
        state: profile.state || '',
        zip: profile.zip || '',
        npi: profile.npi || '',
        specialty: profile.specialty || '',
        website: profile.website || '',
      });
    }
  }, [profile]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await updateProfile(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = () => {
    // ============================================================
    // STRIPE CUSTOMER PORTAL
    // Create a Stripe Customer Portal session from your backend:
    //
    // const res = await fetch('/api/create-portal-session', {
    //   method: 'POST',
    //   body: JSON.stringify({ customerId: profile.stripe_customer_id }),
    // });
    // const { url } = await res.json();
    // window.location.href = url;
    // ============================================================
    alert('Stripe Customer Portal not yet configured.\n\nConnect your Stripe Customer Portal endpoint to enable billing management.');
  };

  const TABS = [
    { id: 'profile', label: 'Practice Profile', icon: Building2 },
    { id: 'contact', label: 'Contact Info', icon: User },
    { id: 'billing', label: 'Subscription & Billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Key },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Account Settings</div>
          <div className="page-subtitle">Manage your practice information and subscription</div>
        </div>
      </div>

      <div className="page-body">
        {/* PROFILE HEADER */}
        <div style={{
          background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '20px 24px', marginBottom: 24, display: 'flex',
          alignItems: 'center', gap: 16
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%',
            background: '#e8f0f7', color: '#10497c',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, flexShrink: 0
          }}>
            {(form.full_name || user?.email || 'P').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>
              {form.full_name || 'Your Name'}
            </div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {form.practice_name || 'Your Practice'} · {user?.email}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="badge badge-teal">
              ✓ {profile?.subscription_tier || 'Preferred Provider'}
            </span>
          </div>
        </div>

        <div className="tabs">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={13} style={{ marginRight: 5 }} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <form className="form-card" onSubmit={handleSaveProfile}>
            {saved && (
              <div className="alert alert-success mb-16">
                <CheckCircle size={14} /> Profile updated successfully!
              </div>
            )}
            {error && <div className="alert alert-error mb-16"><AlertCircle size={14} />{error}</div>}

            <div className="form-section-title">Practice Information</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Provider Full Name <span className="required">*</span></label>
                <input type="text" className="form-input" value={form.full_name} onChange={set('full_name')} placeholder="Dr. Jane Smith" required />
              </div>
              <div className="form-group">
                <label className="form-label">Practice Name <span className="required">*</span></label>
                <input type="text" className="form-input" value={form.practice_name} onChange={set('practice_name')} placeholder="Smith Pain & Wellness" required />
              </div>
              <div className="form-group">
                <label className="form-label">Medical Specialty</label>
                <input type="text" className="form-input" value={form.specialty} onChange={set('specialty')} placeholder="e.g. Pain Management, Physical Therapy" />
              </div>
              <div className="form-group">
                <label className="form-label">NPI Number</label>
                <input type="text" className="form-input" value={form.npi} onChange={set('npi')} placeholder="10-digit NPI" maxLength={10} />
                <span className="form-hint">National Provider Identifier for billing records</span>
              </div>
              <div className="form-group full">
                <label className="form-label">Practice Website</label>
                <input type="url" className="form-input" value={form.website} onChange={set('website')} placeholder="https://yourpractice.com" />
              </div>
            </div>

            <div className="divider" />
            <div className="form-section-title">Practice Address</div>
            <div className="form-grid">
              <div className="form-group full">
                <label className="form-label">Street Address</label>
                <input type="text" className="form-input" value={form.address} onChange={set('address')} placeholder="123 Medical Dr, Suite 100" />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input type="text" className="form-input" value={form.city} onChange={set('city')} placeholder="Dallas" />
              </div>
              <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">State</label>
                  <select className="form-select" value={form.state} onChange={set('state')}>
                    <option value="">State</option>
                    {US_STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">ZIP Code</label>
                  <input type="text" className="form-input" value={form.zip} onChange={set('zip')} placeholder="75201" maxLength={10} />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? (
                  <><div className="loading-spinner" style={{ width: 14, height: 14, borderWidth: 2, margin: 0 }} /> Saving...</>
                ) : '✓ Save Profile'}
              </button>
            </div>
          </form>
        )}

        {/* CONTACT TAB */}
        {activeTab === 'contact' && (
          <form className="form-card" onSubmit={handleSaveProfile}>
            {saved && <div className="alert alert-success mb-16"><CheckCircle size={14} /> Contact info updated!</div>}
            {error && <div className="alert alert-error mb-16"><AlertCircle size={14} />{error}</div>}

            <div className="form-section-title">Contact Details</div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" className="form-input" value={user?.email || ''} disabled
                  style={{ background: '#f8fafc', color: '#94a3b8' }} />
                <span className="form-hint">To change email, contact support@neurogenx.com</span>
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input type="tel" className="form-input" value={form.phone} onChange={set('phone')} placeholder="(555) 000-0000" />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : '✓ Save Changes'}
              </button>
            </div>
          </form>
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && (
          <div>
            <div className="subscription-card active-plan">
              <div>
                <div style={{ fontSize: 11, color: '#21999d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Current Plan</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>Preferred Provider</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                  Includes: Supply discounts, marketing materials, clinical resources, priority support
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <span className="badge badge-active" style={{ fontSize: 12, padding: '4px 12px' }}>Active</span>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Billed via Stripe</div>
              </div>
            </div>

            <div style={{
              background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
              padding: '20px 24px', marginBottom: 16
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Manage Subscription & Payment</div>
              <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, lineHeight: 1.5 }}>
                Update your payment method, view invoices, or cancel your subscription through the Stripe Customer Portal.
              </div>
              <button className="btn btn-primary" onClick={handleManageBilling}>
                <CreditCard size={14} />
                Open Billing Portal
                <ExternalLink size={12} />
              </button>
            </div>

            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontWeight: 500, fontSize: 13, color: '#92400e', marginBottom: 4 }}>⚠ Stripe Portal Not Yet Configured</div>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                Set up a Stripe Customer Portal session endpoint in your backend. See comments in <code>AccountPage.jsx</code> for instructions.
              </div>
            </div>
          </div>
        )}

        {/* SECURITY TAB */}
        {activeTab === 'security' && (
          <div className="form-card">
            <div className="form-section-title">Password & Security</div>
            <div className="alert alert-info mb-16">
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              Password management is handled through Supabase Auth. Use the button below to send a reset link.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button className="btn btn-outline" style={{ maxWidth: 280 }}>
                <Key size={14} />
                Send Password Reset Email
              </button>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                A reset link will be sent to <strong>{user?.email}</strong>
              </div>
            </div>

            <div className="divider" />
            <div className="form-section-title">Session</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>
              You are currently signed in on this device. All sessions are secured via Supabase Auth with JWT tokens.
            </div>
          </div>
        )}
      </div>
    </>
  );
}
